/**
 * External dependencies
 */
import throttle from 'lodash/throttle';
import type {
	ConfettiOption,
	ConfettiType,
	Dict,
	HeatmapFrameAction,
	ClickWithVisibility,
} from '@nab/types';

/**
 * Internal dependencies
 */
import { STATS_REFRESH_DELAY } from './internal/constants';
import {
	areRawResults,
	getActiveConfettiFilter,
	getConfettiFilterOptions,
	getConfettiClicks,
	getIFrameLabels,
	getOpacity,
	processResults,
	isInteractive,
} from './internal/store';
import {
	createElement,
	createOverlayElement,
	maximizeElement,
} from './internal/utils';

const confetti:
	| Record< string, never >
	| {
			canvas: HTMLCanvasElement;
			overlay: HTMLElement;
			stats: HTMLElement;
			statsTracker: HTMLElement;
	  } = {};

export function reactToParentMessages( action: HeatmapFrameAction ): true {
	if ( 'confetti' !== action.mode ) {
		removeConfettiFromBody();
		return true;
	}

	if ( ! areRawResults( [ 'still-loading', 'canceling', 'ready' ] ) ) {
		return true;
	}

	switch ( action.type ) {
		case 'process-results':
			processResults();
			return true;

		case 'render-results':
			renderResults();
			return true;

		case 'update-opacity':
			setOverlayOpacity();
			return true;

		case 'make-interactive':
			makeConfettiInteractive();
			processResults();
			return true;
	}
}

// =======
// HELPERS
// =======

function renderResults() {
	if ( ! confetti.canvas ) {
		initConfetti();
	}

	setOverlayOpacity();
	makeConfettiInteractive();
	appendConfettiInBody();
	renderConfetti();
}

function makeConfettiInteractive() {
	if ( ! confetti ) {
		return;
	}

	const i = isInteractive();
	confetti.overlay.style.pointerEvents = i ? 'none' : '';
	confetti.canvas.style.pointerEvents = i ? 'none' : '';
	confetti.stats.style.pointerEvents = i ? 'none' : '';
	confetti.statsTracker.style.pointerEvents = i ? 'none' : '';
}

function setOverlayOpacity() {
	if ( ! confetti.overlay ) {
		return;
	}
	const opacity = getOpacity();
	confetti.overlay.style.opacity = `${ opacity / 100 }`;
}

function removeConfettiFromBody() {
	if ( document.body === confetti.canvas?.parentNode ) {
		document.body.removeChild( confetti.canvas );
	}

	if ( document.body === confetti.overlay?.parentNode ) {
		document.body.removeChild( confetti.overlay );
	}

	if ( document.body === confetti.stats?.parentNode ) {
		document.body.removeChild( confetti.stats );
	}

	if ( document.body === confetti.statsTracker?.parentNode ) {
		document.body.removeChild( confetti.statsTracker );
	}
}

function appendConfettiInBody() {
	if ( ! confetti ) {
		return;
	}

	maximizeElement( confetti.overlay );
	maximizeElement( confetti.canvas );

	if ( document.body === confetti.canvas.parentNode ) {
		return;
	}

	document.body.appendChild( confetti.overlay );
	document.body.appendChild( confetti.canvas );
	document.body.appendChild( confetti.stats );
	document.body.appendChild( confetti.statsTracker );
}

function initConfetti() {
	confetti.overlay = createOverlayElement( 'div' );
	confetti.overlay.style.background = '#000';
	confetti.canvas = createOverlayElement< HTMLCanvasElement >( 'canvas' );

	// Stats.
	confetti.stats = createOverlayElement( 'div' );
	confetti.stats.style.height = 'auto';
	confetti.stats.style.borderBottom = '2px dashed #262b2d';
	confetti.stats.style.opacity = '0';
	confetti.stats.style.transition = 'opacity 200ms ease-in-out';

	const scrollStatsContainer = createElement( 'span', {
		style: {
			fontSize: '13px',
			left: '1em',
			borderRadius: '4px',
			fontWeight: '600',
			fontFamily:
				'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
			background: '#262b2d',
			pointerEvents: 'none',
			color: '#eee',
			display: 'inline-block',
			position: 'absolute',
			transform: 'translate(0, -50%)',
			whiteSpace: 'nowrap',
		},
	} );

	const statsPopularity = createElement( 'span', {
		className: 'nab-data-block',
		style: { padding: '0 1em' },
	} );
	const statsPopularityLabel = document.createTextNode(
		getIFrameLabels().clicksAbove + ' '
	);
	const statsPopularityValue = createElement( 'output', {
		id: 'nab-clicks-above',
	} );
	statsPopularityValue.style.color = 'inherit';
	statsPopularityValue.style.display = 'inline';
	statsPopularityValue.style.fontSize = 'inherit';
	statsPopularityValue.style.lineHeight = 'inherit';
	statsPopularityValue.style.margin = '0';
	statsPopularityValue.style.padding = '0';

	scrollStatsContainer.appendChild( statsPopularity );
	statsPopularity.appendChild( statsPopularityLabel );
	statsPopularity.appendChild( statsPopularityValue );

	// Stats tracker.
	confetti.statsTracker = createOverlayElement( 'div' );

	// Event listeners.
	confetti.stats.appendChild( scrollStatsContainer );

	confetti.statsTracker.addEventListener( 'mouseenter', () => {
		if ( confetti.stats ) {
			confetti.stats.style.opacity = '1';
		}
	} );

	confetti.statsTracker.addEventListener( 'mouseleave', () => {
		if ( confetti.stats ) {
			confetti.stats.style.opacity = '0';
		}
	} );

	// Update positon.
	confetti.statsTracker.addEventListener( 'mousemove', ( event ) => {
		if ( ! confetti.stats ) {
			return;
		}
		confetti.stats.style.top = `${ Math.max( event.pageY, 0 ) }px`;
	} );

	// Update count.
	confetti.statsTracker.addEventListener(
		'mousemove',
		throttle( ( event: MouseEvent ) => {
			const clicksEl = document.getElementById( 'nab-clicks-above' );
			if ( ! clicksEl ) {
				return;
			}
			const clicks = getConfettiClicks().filter(
				( click ) =>
					click.visibility === 'visible' &&
					( click.realCoordinates.y || Number.POSITIVE_INFINITY ) <=
						event.pageY
			);
			clicksEl.textContent = `${ clicks.length }`;
		}, STATS_REFRESH_DELAY )
	);
}

function renderConfetti() {
	if ( ! confetti.canvas ) {
		return;
	}

	const ctx = confetti.canvas.getContext( '2d' );
	if ( ! ctx ) {
		return;
	}

	ctx.clearRect( 0, 0, confetti.canvas.width, confetti.canvas.height );
	ctx.lineWidth = 1;
	ctx.strokeStyle = '#000';

	const activeFilter = getActiveConfettiFilter();
	const options = getConfettiFilterOptions();
	getConfettiClicks().forEach( ( c ) =>
		renderClick( ctx, activeFilter, options, c )
	);
}

function renderClick(
	ctx: CanvasRenderingContext2D,
	activeFilter: ConfettiType,
	filterOptions: Partial< Record< ConfettiType, Dict< ConfettiOption > > >,
	click: ClickWithVisibility
) {
	if ( 'hidden' === click.visibility ) {
		return;
	}

	const { x, y } = click.realCoordinates;
	const activeOptions = filterOptions[ activeFilter ] ?? {};

	ctx.fillStyle = activeOptions[ click[ activeFilter ] ]?.color || '#ec82b6';
	ctx.beginPath();
	ctx.arc( x, y, 6, 0, 2 * Math.PI, true );
	ctx.closePath();
	ctx.fill();
	ctx.stroke();
}
